import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { PeriodSelector, PlanPrice, PlanFeatures, FounderBanner, EnterpriseCard, ContactLine } from "@/components/PricingParts";
import { Logo } from "@/lib/brand";
import { SiteFooter } from "@/components/SiteFooter";
import {
  ArrowRight, Check, ForkKnife, HardHat, Storefront, Briefcase, CashRegister, Receipt, Users, ChartBar, Wallet, Globe,
  FileText, Ruler, Package, CalendarCheck, Clock, Bank, WarningCircle,
} from "@phosphor-icons/react";

const CTA = ({ className = "", testid = "cta-trial" }) => (
  <Link to="/register" data-testid={testid}
    className={`group inline-flex items-center gap-2 rounded-full bg-mint px-7 py-4 text-base font-bold text-mint-ink shadow-lg shadow-mint/25 transition-transform hover:-translate-y-0.5 ${className}`}>
    Experimentar 30 dias grátis <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
  </Link>
);

const COMMON = [
  [Users, "Clientes"], [Receipt, "Vendas: pró-formas e recibos"], [CashRegister, "Caixa com saldo diário"], [Wallet, "Despesas por categoria"],
  [FileText, "Orçamentos em PDF"], [ChartBar, "Relatórios e CSV"],
];

function SectorCard({ img, icon: Icon, title, items, testid }) {
  return (
    <div className="group overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur" data-testid={testid}>
      <div className="relative h-52 overflow-hidden">
        <img src={img} alt={title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-petrol to-transparent" />
        <div className="absolute bottom-4 left-5 flex items-center gap-2 text-white">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-mint text-mint-ink"><Icon size={22} weight="bold" /></span>
          <span className="font-display text-2xl font-extrabold">{title}</span>
        </div>
      </div>
      <ul className="space-y-2.5 p-6">
        {items.map(([I, t]) => <li key={t} className="flex items-center gap-3 text-white/85"><I size={18} className="text-mint" /> {t}</li>)}
      </ul>
    </div>
  );
}

function Plans() {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(12);
  useEffect(() => {
    axios.get(`${API}/public/plans`).then((r) => { setData(r.data); setMonths(r.data.default_months); }).catch(() => {});
  }, []);
  if (!data) return null;
  const selfServe = data.plans.filter((p) => !p.contact_only);
  const empresa = data.plans.find((p) => p.contact_only);
  return (
    <section id="planos" className="mx-auto max-w-6xl px-5 py-20" data-testid="landing-plans">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-petrol">Planos</p>
          <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-petrol sm:text-4xl">Comece grátis. Pague só depois.</h2>
          <p className="mt-2 max-w-xl text-slate-600">{data.trial_days} dias de teste com tudo incluído. Depois pague {data.durations?.includes(6) ? "ao mês, a cada 6 ou a cada 12 meses" : "ao mês ou a cada 12 meses"} — por transferência, Vinti4 ou cartão.</p>
        </div>
        <PeriodSelector plans={selfServe} durations={data.durations} months={months} setMonths={setMonths} dark />
      </div>
      <div className="mt-8"><FounderBanner founder={data.founder} dark /></div>
      <div className={`mx-auto mt-6 grid gap-5 ${empresa ? "md:grid-cols-3" : "max-w-4xl md:grid-cols-2"}`}>
        {selfServe.map((p) => (
          <div key={p.key} className={`flex flex-col rounded-3xl border p-7 ${p.highlight ? "border-petrol bg-petrol text-white" : "border-slate-200 bg-white"}`} data-testid={`landing-plan-${p.key}`}>
            <div className="mb-4 flex items-center justify-between gap-2">
              <h3 className="font-display text-xl font-extrabold">{p.name}</h3>
              {p.founder_prices && <span className="rounded-full bg-warn px-2.5 py-1 text-[11px] font-extrabold text-ink">Preço Fundador</span>}
            </div>
            <PlanPrice plan={p} months={months} muted={p.highlight ? "text-white/60" : "text-slate-500"} />
            <PlanFeatures features={p.features} iconClass={p.highlight ? "text-mint" : "text-petrol"} />
            <Link to="/register" className={`mt-7 rounded-full py-3 text-center font-bold transition-colors ${p.highlight ? "bg-mint text-mint-ink hover:bg-white hover:text-petrol" : "bg-canvas text-petrol hover:bg-slate-200"}`}
              data-testid={`landing-plan-cta-${p.key}`}>Começar teste grátis</Link>
          </div>
        ))}
        {empresa ? <EnterpriseCard plan={empresa} contact={data.contact} /> : <ContactLine contact={data.contact} className="md:col-span-2" />}
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-ink" data-testid="landing-page">
      <header className="absolute inset-x-0 top-0 z-20">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5">
          <Link to="/" aria-label="GESTA CV — início" data-testid="landing-home-link"><Logo onDark className="h-9 w-auto" testid="landing-logo-white" /></Link>
          <nav className="flex items-center gap-2 text-sm font-semibold text-white">
            <a href="#planos" className="hidden rounded-full px-4 py-2 hover:bg-white/10 sm:block">Planos</a>
            <Link to="/login" className="rounded-full px-4 py-2 hover:bg-white/10" data-testid="landing-login">Entrar</Link>
            <Link to="/register" className="hidden rounded-full bg-white px-4 py-2 text-petrol sm:block" data-testid="landing-register">Criar conta</Link>
          </nav>
        </div>
      </header>

      <section className="relative overflow-hidden bg-petrol pb-24 pt-36 text-white">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-mint/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-52 left-10 h-[420px] w-[420px] rounded-full bg-mint/10 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-5">
          <p className="stagger-item inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-white/80">Feito para PME de Cabo Verde · CVE</p>
          <h1 className="stagger-item mt-6 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }}>
            A gestão da sua empresa, <span className="text-mint">do caixa à obra</span>, num só lugar.
          </h1>
          <p className="stagger-item mt-6 max-w-2xl text-base text-white/70 md:text-lg" style={{ animationDelay: "160ms" }}>
            Clientes, vendas, caixa, despesas, orçamentos e relatórios — com módulos próprios para restauração e construção. Em português, em escudos.
          </p>
          <div className="stagger-item mt-9 flex flex-wrap items-center gap-4" style={{ animationDelay: "240ms" }}>
            <CTA />
            <span className="text-sm text-white/60">Sem cartão · Escolha o plano só no fim do teste</span>
          </div>
        </div>
      </section>

      <section className="bg-petrol pb-24">
        <div className="mx-auto grid max-w-6xl gap-6 px-5 md:grid-cols-2">
          <SectorCard testid="sector-restauracao" img="/img/restauracao.jpg" icon={ForkKnife} title="Restauração"
            items={[[CashRegister, "Caixa e vendas rápidas"], [Receipt, "Recibos por WhatsApp"], [Wallet, "Despesas e lucro do mês"], [Users, "Clientes"]]} />
          <SectorCard testid="sector-construcao" img="/img/construcao.jpg" icon={HardHat} title="Construção"
            items={[[HardHat, "Obras com progresso e fotos"], [Ruler, "Orçamentos por m² em PDF"], [Package, "Materiais e gastos por obra"], [ChartBar, "Lucro por obra"]]} />
        </div>
        <div className="mx-auto mt-6 max-w-6xl px-5">
          <div className="flex flex-wrap items-center gap-4 rounded-3xl border border-white/10 p-6 text-white/80">
            <div className="flex gap-2"><Storefront size={22} className="text-mint" /><Briefcase size={22} className="text-mint" /></div>
            <span><b className="text-white">Comércio e Serviços:</b> em breve.</span>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-20">
        <p className="text-sm font-bold uppercase tracking-[0.2em] text-petrol">Incluído em todos os setores</p>
        <h2 className="mt-2 max-w-2xl font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Tudo o que precisa no dia a dia</h2>
        <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
          {COMMON.map(([I, t], i) => (
            <div key={t} className="stagger-item rounded-2xl border border-slate-200 p-5 transition-colors hover:border-mint" style={{ animationDelay: `${i * 50}ms` }}>
              <I size={26} className="text-petrol" />
              <div className="mt-3 font-semibold">{t}</div>
            </div>
          ))}
          <div className="rounded-2xl bg-slate-50 p-5">
            <Bank size={26} className="text-petrol" />
            <div className="mt-3 font-semibold">Pagamento por transferência ou Vinti4</div>
          </div>
        </div>
        <div className="mt-8 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900" data-testid="landing-fiscal-note">
          <WarningCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />
          <p>Até à certificação e-Fatura da DNRE, o GESTA CV emite <b>orçamentos, faturas pró-forma e recibos internos</b> — documentos sem valor fiscal.</p>
        </div>
      </section>

      <Plans />

      <section className="px-5 pb-20">
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-6 rounded-[2rem] bg-petrol p-10 text-white md:flex-row md:items-center md:justify-between md:p-14">
          <div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight">Experimente sem compromisso.</h2>
            <p className="mt-2 text-white/80">Registe a empresa em 2 minutos. Os seus dados ficam sempre guardados.</p>
          </div>
          <CTA testid="cta-trial-bottom" />
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
